import { useState } from 'react';
import * as Sentry from "@sentry/react";
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

export default function TestSentry() {
  const [results, setResults] = useState<Array<{ type: string; success: boolean; message: string; eventId?: string }>>([]);

  const testMessage = async () => {
    try {
      const eventId = Sentry.captureMessage('Test message from Sentry test page', 'info');
      
      // Force flush to send immediately
      await Sentry.flush(2000);
      
      setResults(prev => [...prev, {
        type: 'Message',
        success: true,
        message: 'Successfully sent test message to Sentry (with flush)',
        eventId
      }]);
    } catch (error) {
      setResults(prev => [...prev, {
        type: 'Message',
        success: false,
        message: `Failed to send message: ${error}`
      }]);
    }
  };

  const testError = async () => {
    try {
      const eventId = Sentry.captureException(new Error('Test error from Sentry test page - ' + new Date().toISOString()));
      
      // Force flush to send immediately
      await Sentry.flush(2000);
      
      setResults(prev => [...prev, {
        type: 'Error',
        success: true,
        message: 'Successfully captured test error (with flush)',
        eventId
      }]);
    } catch (error) {
      setResults(prev => [...prev, {
        type: 'Error',
        success: false,
        message: `Failed to capture error: ${error}`
      }]);
    }
  };

  const testThrow = () => {
    try {
      setResults(prev => [...prev, {
        type: 'Thrown Error',
        success: true,
        message: 'About to throw error - check Sentry in a few seconds'
      }]);
      // Throw error after a delay so the message appears first
      setTimeout(() => {
        throw new Error('Test thrown error - should be caught by error boundary');
      }, 500);
    } catch (error) {
      setResults(prev => [...prev, {
        type: 'Thrown Error',
        success: false,
        message: `Failed: ${error}`
      }]);
    }
  };

  const checkConfig = () => {
    const client = Sentry.getClient();
    const hasClient = !!client;
    const dsn = client?.getDsn()?.toString() || 'Not configured';
    
    setResults(prev => [...prev, {
      type: 'Configuration',
      success: hasClient,
      message: hasClient 
        ? `Sentry is initialized. DSN: ${dsn.substring(0, 50)}...` 
        : 'Sentry client not initialized'
    }]);
  };

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="max-w-4xl mx-auto space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Sentry Integration Test</CardTitle>
            <CardDescription>
              Test Sentry error tracking and verify events are being sent to your Sentry project
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <Button onClick={checkConfig} variant="outline">
                Check Configuration
              </Button>
              <Button onClick={testMessage} variant="outline">
                Send Test Message
              </Button>
              <Button onClick={testError} variant="outline">
                Capture Test Error
              </Button>
              <Button onClick={testThrow} variant="destructive">
                Throw Test Error
              </Button>
            </div>

            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertTitle>Instructions</AlertTitle>
              <AlertDescription>
                1. Check configuration first to verify Sentry is initialized<br />
                2. Send test message or error<br />
                3. Check your Sentry dashboard at sentry.io for the events<br />
                4. Event IDs will appear below when actions are successful
              </AlertDescription>
            </Alert>
          </CardContent>
        </Card>

        {results.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>Test Results</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {results.map((result, index) => (
                <div 
                  key={index}
                  className="flex items-start gap-3 p-3 rounded-lg border"
                >
                  {result.success ? (
                    <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
                  )}
                  <div className="flex-1 space-y-1">
                    <p className="font-medium">{result.type}</p>
                    <p className="text-sm text-muted-foreground">{result.message}</p>
                    {result.eventId && (
                      <p className="text-xs font-mono bg-muted p-2 rounded">
                        Event ID: {result.eventId}
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Current Environment</CardTitle>
          </CardHeader>
          <CardContent>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="font-medium">Mode:</dt>
                <dd className="text-muted-foreground">{import.meta.env.MODE}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="font-medium">Sentry Client:</dt>
                <dd className="text-muted-foreground">
                  {Sentry.getClient() ? 'Initialized' : 'Not Initialized'}
                </dd>
              </div>
            </dl>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
